/**
 * What every surface is made of, and the one shader patch that makes level
 * geometry textureable at all.
 *
 * ===========================================================================
 * THE UV PROBLEM
 * ===========================================================================
 *
 * `scene.ts` draws all level geometry as one shared unit `BoxGeometry` under
 * per-instance matrices — that is the instancing work the README's 81-to-10
 * draw-call table describes, and it is not negotiable. But a unit cube's UVs
 * run 0..1 per face, so scaling it to a 20m x 3m wall stretches one texture
 * across 20 metres. Texel density would differ on every box, and a wall run
 * that `mergeRects` merged out of three rectangles would show three different
 * stretches with visible joins between them.
 *
 * So UVs are computed from *world position* in the fragment shader instead.
 * Every level solid is axis-aligned and never rotated, so picking the dominant
 * axis of the face normal is enough — no three-way triplanar blend. Texel
 * density becomes constant everywhere and adjacent boxes tile seamlessly,
 * because they are sampling the same world-space function.
 *
 * Two things fall out of that for free:
 *
 *  - **Trim without geometry.** On a vertical face the V coordinate *is* world
 *    Y in metres, so a wall texture authored for exactly the level's storey
 *    height (see `textures.wallTexture`) puts its skirting board on the floor
 *    and its picture rail under the ceiling, on every wall in the level, with
 *    no boxes of trim to collide with. `wrapT` is `ClampToEdgeWrapping`, which
 *    is GPU sampler state and applies regardless of where the UV came from —
 *    there is deliberately no `clamp()` in the GLSL below.
 *  - **Two materials, one draw call.** A desk partition wants laminate on top
 *    and fabric on the sides. `BoxGeometry` really does define six material
 *    groups, and an `InstancedMesh` with a material array costs one full draw
 *    call per group — six in the main pass and six more in the shadow pass.
 *    Selecting on the face normal in a shader we are already patching costs one
 *    extra sampler.
 *
 * ===========================================================================
 * WHY LAMBERT FOR THE BIG SURFACES
 * ===========================================================================
 *
 * `lights_fragment_begin` unrolls its light loop at compile time and evaluates
 * every light for every fragment — there is no culling of any kind in the
 * forward renderer, so the ceiling's dozen fittings are a dozen evaluations per
 * pixel whether or not that pixel is anywhere near one. On floors and walls,
 * which are most of the screen, `MeshLambertMaterial`'s single N·L is worth
 * having over `MeshStandardMaterial`'s full GGX, and carpet and emulsion have
 * no specular worth modelling anyway. Props are small on screen and are the
 * things you are supposed to look *at*, so they get Standard.
 *
 * Both material classes compile from the same chunk names, so `boxProjected`
 * below is agnostic between them.
 */

import * as THREE from "three";
import type { PropDef } from "@prop-hunt/shared";
import {
  carpetTexture,
  ceilingTexture,
  ceramicTexture,
  concreteTexture,
  fabricTexture,
  foliageTexture,
  laminateTexture,
  metalTexture,
  panelTexture,
  plasticTexture,
  wallTexture,
  woodTexture,
} from "./textures.js";

// ---------------------------------------------------------------------------
// Textures
// ---------------------------------------------------------------------------

/**
 * Wrap a generated canvas as a colour map.
 *
 * `SRGBColorSpace` is not optional and is not the default: `Texture`'s
 * constructor leaves `colorSpace` as `NoColorSpace`, and an undecoded colour
 * map under tone mapping reads as washed-out and oddly bright. The decode is
 * done by the sampler from this property alone, so it works exactly as well
 * for the custom uniforms `boxProjected` binds as it would for `material.map`.
 * Mipmaps are generated for correct LOD filtering on surfaces viewed at steep angles.
 */
export function colorMap(
  canvas: HTMLCanvasElement,
  anisotropy: number,
  wrapT: THREE.Wrapping = THREE.RepeatWrapping,
): THREE.CanvasTexture {
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = wrapT;
  texture.anisotropy = anisotropy;
  // Mipmaps are critical for surfaces viewed at grazing angles; carpet especially
  // shimmers badly without them. Canvas textures generate full chains from power-of-two sizes.
  texture.generateMipmaps = true;
  return texture;
}

// ---------------------------------------------------------------------------
// The world-space projection
// ---------------------------------------------------------------------------

export interface BoxProjection {
  /** Sampled where the face's dominant world axis is X or Z. */
  side: THREE.Texture;
  /** Sampled where it is Y. Pass `side` again to disable the distinction. */
  top: THREE.Texture;
  /** Metres per horizontal repeat of `side`. */
  sideRepeat: number;
  /**
   * Metres `side`'s V axis spans upward from y=0. Set to the storey height and
   * `side` becomes an elevation rather than a tiling swatch — see the note on
   * trim above, and `textures.wallTexture`.
   */
  sideHeight: number;
  /** Metres per repeat of `top`, on both axes. */
  topRepeat: number;
  /** Distinguishes compiled programs. Equal keys share one. */
  cacheKey: string;
}

/**
 * Patch a material to take its UVs from world position rather than geometry.
 *
 * Notes on the injection points, all of which are load-bearing:
 *
 *  - `<project_vertex>` and not `<begin_vertex>`. `begin_vertex` only declares
 *    `transformed`; morph, skinning and displacement all run after it. By
 *    `project_vertex` the position is final and `objectNormal` is still in
 *    scope.
 *  - The world position is computed here rather than taken from three's own
 *    `<worldpos_vertex>`, whose entire body sits behind
 *    `#if defined(USE_ENVMAP) || defined(USE_SHADOWMAP) || ...`. It would
 *    happen to be defined today because shadows are on, and would silently
 *    vanish the day someone turned them off.
 *  - `material.map` is deliberately *not* set. Doing so would make
 *    `<map_fragment>` sample `vMapUv`, and would drag the texture into the
 *    shadow depth material, which has no use for it. The `#include` token is
 *    still present in the source when `onBeforeCompile` runs — includes are
 *    resolved later — so replacing it works even though it would expand to
 *    nothing.
 *  - Both textures are sampled unconditionally and mixed. The branch is uniform
 *    within a triangle but not across a quad at a silhouette, and implicit-LOD
 *    texture fetches inside non-uniform control flow are undefined.
 *
 * The shadow depth pass copies only a fixed handful of properties from the
 * material and never `onBeforeCompile`, so none of this reaches it. That is
 * correct: depth does not care what colour a wall is.
 */
export function boxProjected<M extends THREE.Material>(material: M, p: BoxProjection): M {
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uSideMap = { value: p.side };
    shader.uniforms.uTopMap = { value: p.top };
    shader.uniforms.uProject = {
      value: new THREE.Vector3(1 / p.sideRepeat, 1 / p.sideHeight, 1 / p.topRepeat),
    };

    shader.vertexShader = shader.vertexShader
      .replace(
        "#include <common>",
        `#include <common>
varying vec3 vBoxPos;
varying vec3 vBoxNormal;`,
      )
      .replace(
        "#include <project_vertex>",
        `{
  vec4 boxPos = vec4( transformed, 1.0 );
  vec3 boxNrm = objectNormal;
  #ifdef USE_INSTANCING
    boxPos = instanceMatrix * boxPos;
    // A box's objectNormal is always a local axis and instanceMatrix is R*S
    // with axis-aligned S, so R*S*n stays parallel to R*n. No inverse-transpose
    // is needed for this; three's own <defaultnormal_vertex> still does the
    // proper correction for the normal lighting uses.
    boxNrm = mat3( instanceMatrix ) * boxNrm;
  #endif
  vBoxPos = ( modelMatrix * boxPos ).xyz;
  vBoxNormal = normalize( mat3( modelMatrix ) * boxNrm );
}
#include <project_vertex>`,
      );

    shader.fragmentShader = shader.fragmentShader
      .replace(
        "#include <common>",
        `#include <common>
varying vec3 vBoxPos;
varying vec3 vBoxNormal;
uniform sampler2D uSideMap;
uniform sampler2D uTopMap;
uniform vec3 uProject;`,
      )
      .replace(
        "#include <map_fragment>",
        `{
  vec3 an = abs( normalize( vBoxNormal ) );
  // U runs along whichever horizontal axis the face spans; V is world Y in
  // metres, so merged wall rects are seamless and trim lands at real heights.
  float u = ( an.x > an.z ) ? vBoxPos.z : vBoxPos.x;
  vec2 sideUv = vec2( u * uProject.x, vBoxPos.y * uProject.y );
  vec2 topUv = vBoxPos.xz * uProject.z;
  diffuseColor *= mix(
    texture2D( uSideMap, sideUv ),
    texture2D( uTopMap, topUv ),
    step( 0.5, an.y )
  );
}`,
      );
  };

  // The default cache key is `onBeforeCompile.toString()`, which is identical
  // for every closure this factory makes — correct only while every varying
  // parameter travels as a uniform, and silently wrong the moment a constant is
  // interpolated into the GLSL above. Being explicit costs nothing and removes
  // the trap.
  material.customProgramCacheKey = () => `boxproj:${p.cacheKey}`;
  return material;
}

// ---------------------------------------------------------------------------
// Themes
// ---------------------------------------------------------------------------

/**
 * A level's palette, keyed by `GameMap.name`.
 *
 * Client-side and derived, exactly like the `PROP_COLORS`-by-`mesh` lookup this
 * grew out of. Appearance deliberately does not live in the map format: a
 * `MapSolid` is six numbers, `mapHash` covers all of them, and a surface that
 * exists only to be looked at has no business in the join handshake.
 */
export interface Theme {
  sky: number;
  fogNear: number;
  fogFar: number;
  floor: () => HTMLCanvasElement;
  /** Metres per repeat of the floor texture. */
  floorRepeat: number;
  /** Built against the level's own storey height. */
  wall: (height: number) => HTMLCanvasElement;
  wallRepeat: number;
  coverTop: () => HTMLCanvasElement;
  coverSide: () => HTMLCanvasElement;
  coverRepeat: number;
  /**
   * `null` for a level that is outdoors: no lid, no light fittings hanging off
   * it, and `sky` is a sky rather than the colour of a ceiling seen edge-on.
   * A roof over a rooftop would be a strange thing to stand on.
   */
  ceiling: (() => HTMLCanvasElement) | null;
  ceilingRepeat: number;
  /** Hemisphere fill: the ceiling's colour and the floor's bounce. */
  hemiSky: number;
  hemiGround: number;
  hemiIntensity: number;
  /** The overhead key light that casts the level's shadows. */
  sun: number;
  sunIntensity: number;
  /** Colour and intensity of a single ceiling fitting. */
  fixture: number;
  fixtureIntensity: number;
}

/**
 * office: warm grey carpet tile, off-white emulsion, a suspended ceiling.
 *
 * Fog is kept far and light: this is a hiding game, and every metre of murk is
 * a metre of advantage handed to the hiders. It exists to give the corridor
 * depth, not to set a mood.
 */
const OFFICE: Theme = {
  sky: 0x141a22,
  fogNear: 26,
  fogFar: 78,
  floor: () => carpetTexture("#4a4f57", "#3a3e45"),
  floorRepeat: 4,
  wall: (height) => wallTexture(height, "#b9b3a6", "#4a4640", "#8d8578"),
  wallRepeat: 4,
  coverTop: () => laminateTexture("#c8bda6"),
  coverSide: () => fabricTexture("#5d6b7a"),
  coverRepeat: 2,
  ceiling: () => ceilingTexture("#d5d3cc", "#9c9a94"),
  ceilingRepeat: 4,
  hemiSky: 0xdfe6f0,
  hemiGround: 0x6b5f52,
  hemiIntensity: 1.15,
  sun: 0xfff3e0,
  sunIntensity: 1.5,
  fixture: 0xfff2d8,
  fixtureIntensity: 11,
};

/**
 * The fallback, which is depot's until someone art-directs a warehouse.
 *
 * Concrete and painted steel rather than carpet and emulsion, so a level that
 * is not office does not arrive wearing office's clothes; everything else —
 * shadows, the ceiling, real prop meshes — it inherits unchanged.
 */
const FALLBACK: Theme = {
  sky: 0x10161d,
  fogNear: 30,
  fogFar: 96,
  floor: () => concreteTexture("#565b60"),
  floorRepeat: 6,
  wall: (height) => wallTexture(height, "#8a8f94", "#3e4247", "#6d7378"),
  wallRepeat: 4,
  coverTop: () => panelTexture("#7d858c", "rgba(0,0,0,0.16)"),
  coverSide: () => panelTexture("#6f767d", "rgba(0,0,0,0.20)"),
  coverRepeat: 2,
  ceiling: () => ceilingTexture("#b8bcc0", "#84888c"),
  ceilingRepeat: 4,
  hemiSky: 0xd6e2ee,
  hemiGround: 0x55504a,
  hemiIntensity: 1.1,
  sun: 0xfff6ea,
  sunIntensity: 1.55,
  fixture: 0xf2f6ff,
  fixtureIntensity: 12,
};

/**
 * rooftops: asphalt decks and rendered brick, under an open sky.
 *
 * The first outdoor level, which is what `ceiling: null` says. Everything the
 * indoor themes get from the lid — the ambient bounce, the strip lights, the
 * dark "sky" that is really a ceiling seen edge-on — has to come from
 * somewhere else here, so the key light does the work of an actual sun and the
 * hemisphere fill is daylight-blue above and warm asphalt below.
 *
 * Fog is pushed further out than any indoor theme and still exists. It is not
 * for mood: an 88 m level with no walls across it needs *some* depth cue or
 * the far parapet reads as being an arm's length away. Kept thin, because
 * every metre of murk on a map this open is a metre of advantage handed to the
 * hiders.
 */
const ROOFTOPS: Theme = {
  sky: 0x9dbbd8,
  fogNear: 55,
  fogFar: 165,
  floor: () => concreteTexture("#57544f"),
  floorRepeat: 5,
  wall: (height) => wallTexture(height, "#9a8878", "#4c443c", "#7d6d5f"),
  wallRepeat: 5,
  coverTop: () => concreteTexture("#8d8880"),
  coverSide: () => panelTexture("#847f77", "rgba(0,0,0,0.18)"),
  coverRepeat: 2,
  ceiling: null,
  ceilingRepeat: 4,
  hemiSky: 0xbcd6f2,
  hemiGround: 0x6e6357,
  hemiIntensity: 1.5,
  sun: 0xfff0d4,
  sunIntensity: 2.2,
  // Unused while `ceiling` is null — no fittings are built to wear them — but
  // the fields are not optional and a plausible value beats a placeholder.
  fixture: 0xfff2d8,
  fixtureIntensity: 10,
};

const THEMES: Readonly<Record<string, Theme>> = { office: OFFICE, rooftops: ROOFTOPS };

/** A level's palette. Unthemed levels get the fallback, never nothing. */
export function themeFor(mapName: string): Theme {
  return THEMES[mapName] ?? FALLBACK;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/**
 * What a prop is made of, keyed by `PropDef.mesh` — the asset key the catalogue
 * has always carried for exactly this purpose, and the same key
 * `prop-meshes.ts` builds geometry from. A kind that is missing here still
 * renders, in a hue derived from its id, so adding to the catalogue produces a
 * prop that has not been art-directed rather than an invisible one.
 */
export interface Surface {
  texture: () => HTMLCanvasElement;
  /** Multiplied into the texture, so one grey metal serves several props. */
  tint: number;
  roughness: number;
  metalness: number;
}

const SURFACES: Readonly<Record<string, Surface>> = {
  crate: { texture: () => woodTexture("#b07a3c", "rgba(0,0,0,0.35)"), tint: 0xffffff, roughness: 0.85, metalness: 0 },
  crate_small: { texture: () => woodTexture("#d8a86a", "rgba(0,0,0,0.32)"), tint: 0xffffff, roughness: 0.85, metalness: 0 },
  pallet: { texture: () => woodTexture("#8a6a44", "rgba(0,0,0,0.40)"), tint: 0xffffff, roughness: 0.9, metalness: 0 },
  barrel: { texture: () => metalTexture("#3f7fa8"), tint: 0xffffff, roughness: 0.42, metalness: 0.65 },
  cone: { texture: () => plasticTexture("#e06a2a"), tint: 0xffffff, roughness: 0.55, metalness: 0 },
  plant: { texture: () => foliageTexture("#3f7d43"), tint: 0xffffff, roughness: 0.75, metalness: 0 },
  pot: { texture: () => ceramicTexture("#9c5a3c"), tint: 0xffffff, roughness: 0.8, metalness: 0 },
  cabinet: { texture: () => metalTexture("#8d9299"), tint: 0xffffff, roughness: 0.45, metalness: 0.55 },
  cooler: { texture: () => plasticTexture("#dfe7ee"), tint: 0xffffff, roughness: 0.3, metalness: 0.05 },
  chair: { texture: () => fabricTexture("#3d4550"), tint: 0xffffff, roughness: 0.9, metalness: 0 },
  printer: { texture: () => plasticTexture("#cdd2d6"), tint: 0xffffff, roughness: 0.5, metalness: 0.1 },
};

/** Stable, well-separated hues from an id — for props and undisguised players. */
export function colorForId(id: number): number {
  const hue = (id * 0.618033988749895) % 1;
  return new THREE.Color().setHSL(hue, 0.6, 0.55).getHex();
}

/**
 * The material for one prop kind.
 *
 * There is exactly one of these per kind for the life of a level, and both the
 * kind's `InstancedMesh` and any player disguised as it draw with this same
 * object — see the registry in scene.ts. A disguise that is a different shade
 * of crate to the crates beside it is not a cosmetic bug in this game.
 */
export function propMaterial(def: PropDef, anisotropy: number): THREE.MeshStandardMaterial {
  const surface = SURFACES[def.mesh];
  if (surface === undefined) {
    return new THREE.MeshStandardMaterial({ color: colorForId(def.id), roughness: 0.7 });
  }
  const map = colorMap(surface.texture(), anisotropy);
  return new THREE.MeshStandardMaterial({
    map,
    color: surface.tint,
    roughness: surface.roughness,
    metalness: surface.metalness,
  });
}
